import React from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons,
  IonBackButton, IonButton, IonIcon, IonCard, IonCardContent,
  IonCardHeader, IonCardTitle, IonBadge, IonSpinner, IonList, IonItem,
  IonLabel, IonAlert,
} from '@ionic/react';
import { pencil, trash } from 'ionicons/icons';
import { useParams, useHistory } from 'react-router-dom';
import { useCard, useDeleteCard } from '../hooks/useCards';
import { API_BASE } from '../config/api';

const resolveImageUrl = (url?: string) =>
  url && url.startsWith('/uploads') ? `${API_BASE}${url}` : url;

const CardDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: card, isLoading } = useCard(Number(id));
  const deleteCard = useDeleteCard();
  const history = useHistory();
  const [showDelete, setShowDelete] = React.useState(false);

  const handleDelete = async () => {
    await deleteCard.mutateAsync(Number(id));
    history.replace('/cards');
  };

  if (isLoading || !card) {
    return (
      <IonPage>
        <IonHeader><IonToolbar color="primary"><IonButtons slot="start"><IonBackButton defaultHref="/cards" /></IonButtons><IonTitle>Card</IonTitle></IonToolbar></IonHeader>
        <IonContent className="ion-padding ion-text-center"><IonSpinner /></IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start"><IonBackButton defaultHref="/cards" /></IonButtons>
          <IonTitle>{card.name}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => history.push(`/cards/${id}/edit`)}>
              <IonIcon icon={pencil} />
            </IonButton>
            <IonButton onClick={() => setShowDelete(true)} color="danger">
              <IonIcon icon={trash} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>{card.name}</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {card.condition && <IonBadge color="medium">{card.condition}</IonBadge>}
              {card.rarity && <IonBadge color="tertiary">{card.rarity}</IonBadge>}
              {card.is_foil && <IonBadge color="warning">Foil</IonBadge>}
              {card.is_first_edition && <IonBadge color="success">1st Edition</IonBadge>}
            </div>
            <IonList>
              <IonItem><IonLabel>Game</IonLabel><span>{card.game?.name}</span></IonItem>
              {card.set_name && <IonItem><IonLabel>Set</IonLabel><span>{card.set_name}</span></IonItem>}
              {card.card_number && <IonItem><IonLabel>Number</IonLabel><span>{card.card_number}</span></IonItem>}
              <IonItem><IonLabel>Quantity</IonLabel><span>{card.quantity}</span></IonItem>
              {card.estimated_value != null && <IonItem><IonLabel>Value</IonLabel><span>${card.estimated_value.toFixed(2)}</span></IonItem>}
              <IonItem><IonLabel>Language</IonLabel><span>{card.language}</span></IonItem>
              {card.notes && <IonItem><IonLabel>Notes</IonLabel><span>{card.notes}</span></IonItem>}
            </IonList>

            {card.custom_fields && Object.keys(card.custom_fields).length > 0 && (
              <>
                <h3 style={{ marginTop: 16 }}>Game-Specific Fields</h3>
                <IonList>
                  {Object.entries(card.custom_fields).map(([key, value]) => (
                    <IonItem key={key}>
                      <IonLabel>{key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</IonLabel>
                      <span>{Array.isArray(value) ? value.join(', ') : String(value)}</span>
                    </IonItem>
                  ))}
                </IonList>
              </>
            )}
          </IonCardContent>
        </IonCard>

        {(card.image_front_url || card.image_back_url) && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
            {card.image_front_url && (
              <div style={{ textAlign: 'center' }}>
                <img src={resolveImageUrl(card.image_front_url)} alt={`${card.name} front`} style={{ maxWidth: 200, borderRadius: 8 }} />
                <div style={{ fontSize: 12, color: 'var(--ion-color-medium)', marginTop: 4 }}>Front</div>
              </div>
            )}
            {card.image_back_url && (
              <div style={{ textAlign: 'center' }}>
                <img src={resolveImageUrl(card.image_back_url)} alt={`${card.name} back`} style={{ maxWidth: 200, borderRadius: 8 }} />
                <div style={{ fontSize: 12, color: 'var(--ion-color-medium)', marginTop: 4 }}>Back</div>
              </div>
            )}
          </div>
        )}

        <IonAlert
          isOpen={showDelete}
          onDidDismiss={() => setShowDelete(false)}
          header="Delete Card"
          message={`Are you sure you want to delete "${card.name}"?`}
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            { text: 'Delete', role: 'destructive', handler: handleDelete },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default CardDetail;
